"use client";

import { useId } from "react";
import type { Color, PieceSymbol } from "chess.js";
import { CLASSIC_BASE, FINISHES, MODERN, MODERN_BASE, STAUNTON, type PieceFinish, type PieceStyle } from "./piece-shapes";

export type { PieceFinish, PieceStyle } from "./piece-shapes";

export type PieceArtProps = {
  type: PieceSymbol;
  color: Color;
  finish?: PieceFinish;
  style?: PieceStyle;
  className?: string;
};

/** Decorative piece art: the board cell or parent supplies its accessible name. */
export function PieceArt({ type, color, finish = "ivory", style = "staunton", className }: PieceArtProps) {
  const id = `piece-${useId().replace(/:/g, "")}`;
  const palette = FINISHES[finish][color];
  const sculpture = (style === "modern" ? MODERN : STAUNTON)[type];
  const base = style === "modern" ? MODERN_BASE : CLASSIC_BASE;
  const shape = `${sculpture.outline}${base}`;
  const metallic = finish === "metal";

  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 100 100"
      width="100%"
      height="100%"
      className={className}
      aria-hidden="true"
      focusable="false"
      data-piece={`${color}${type}`}
      data-finish={finish}
      data-piece-style={style}
    >
      <defs>
        <linearGradient id={`${id}-body`} x1="22" y1="30" x2="77" y2="48" gradientUnits="userSpaceOnUse">
          <stop offset="0" stopColor={palette.dark} />
          <stop offset="0.23" stopColor={palette.middle} />
          <stop offset="0.4" stopColor={palette.light} />
          <stop offset={metallic ? "0.5" : "0.58"} stopColor={palette.middle} />
          {metallic && <stop offset="0.63" stopColor={palette.light} />}
          <stop offset="0.86" stopColor={palette.dark} />
          <stop offset="1" stopColor={palette.middle} />
        </linearGradient>
        <linearGradient id={`${id}-base`} x1="28" y1="76" x2="74" y2="90" gradientUnits="userSpaceOnUse">
          <stop stopColor={palette.middle} />
          <stop offset="0.32" stopColor={palette.light} />
          <stop offset="0.7" stopColor={palette.middle} />
          <stop offset="1" stopColor={palette.dark} />
        </linearGradient>
        <linearGradient id={`${id}-shade`} x1="50" y1="15" x2="50" y2="91" gradientUnits="userSpaceOnUse">
          <stop stopColor={palette.reflection} stopOpacity="0.12" />
          <stop offset="0.64" stopColor={palette.edge} stopOpacity="0" />
          <stop offset="1" stopColor={palette.edge} stopOpacity="0.22" />
        </linearGradient>
        <clipPath id={`${id}-clip`}><path d={shape} /></clipPath>
        {finish === "wood" && (
          <pattern id={`${id}-grain`} width="25" height="80" patternUnits="userSpaceOnUse">
            <path d="M3-10C18 12-8 36 6 60S7 90 4 100M14-10C25 16 8 26 16 48S26 81 14 100M23-10C8 15 28 38 23 65S15 88 24 100" fill="none" stroke={palette.carving} strokeWidth="0.65" />
            <path d="M7-10C23 15-4 37 10 60S11 90 8 100M17-10C29 15 11 27 19 48S28 82 17 100" fill="none" stroke={palette.reflection} strokeWidth="0.65" />
          </pattern>
        )}
      </defs>

      <ellipse cx="51" cy="91.5" rx="27" ry="5" fill="#07120d" opacity="0.2" />
      <path d={sculpture.outline} fill={`url(#${id}-body)`} stroke={palette.edge} strokeWidth="1.6" strokeLinejoin="round" />
      <path d={base} fill={`url(#${id}-base)`} stroke={palette.edge} strokeWidth="1.6" strokeLinejoin="round" />

      <g clipPath={`url(#${id}-clip)`}>
        <path d={shape} fill={`url(#${id}-shade)`} />
        {finish === "wood" && <rect x="15" y="5" width="70" height="90" fill={`url(#${id}-grain)`} opacity={color === "w" ? "0.27" : "0.32"} />}
        {finish === "marble" && (
          <g fill="none" strokeLinecap="round">
            <path d="M18 14L39 25L43 34L58 42L62 55L82 66M22 57L38 61L46 73L65 80L73 95" stroke={palette.carving} strokeWidth="1.25" opacity="0.26" />
            <path d="M20 16L38 27L41 36L56 44L60 57L80 68M19 59L36 63L44 75L65 83L70 95" stroke={palette.reflection} strokeWidth="0.9" opacity="0.47" />
            <path d="M43 34L38 41L39 49M58 42L68 37L76 39M46 73L43 84L33 90" stroke={palette.carving} strokeWidth="0.6" opacity="0.23" />
          </g>
        )}
        {finish === "metal" && (
          <g stroke={palette.reflection} strokeWidth="0.35" opacity="0.12">
            {[24, 30, 36, 42, 48, 54, 60, 66, 72, 78, 84, 90].map((y) => <path key={y} d={`M20 ${y}H80`} />)}
          </g>
        )}
      </g>

      <path d={sculpture.carving} fill="none" stroke={palette.carving} strokeWidth={style === "modern" ? "1.25" : "1.5"} strokeLinecap="round" strokeLinejoin="round" opacity={color === "w" ? "0.74" : "0.85"} />
      <path d={sculpture.highlight} fill="none" stroke={palette.reflection} strokeWidth="1.45" strokeLinecap="round" strokeLinejoin="round" opacity={color === "w" ? "0.85" : "0.48"} />
      {type === "n" && (
        <>
          <path d={style === "modern" ? "M38 34L42 32L41 35Z" : "M36 34Q39 31 42 33L39 36Z"} fill={palette.edge} />
          <circle cx="39" cy="33.5" r="0.6" fill={palette.reflection} opacity="0.7" />
        </>
      )}
      <path d={style === "modern" ? "M29 84Q50 88 71 84" : "M30 79Q50 82 70 79M27 85Q50 90 73 85"} fill="none" stroke={palette.carving} strokeWidth="1.4" opacity="0.75" />
      <path d="M31 83Q45 86 60 84M30 89Q45 93 60 91" fill="none" stroke={palette.reflection} strokeWidth="1.05" strokeLinecap="round" opacity={color === "w" ? "0.85" : "0.37"} />
    </svg>
  );
}
